import {
  ApiError,
  type BulkActiveCustomersResult,
  type BulkDeleteCustomersResult,
  type CleanupCandidate,
  type CleanupCandidateIds,
  type CleanupScope,
  CUSTOMER_ORIGINS,
  type CustomerDuplicatePairsPage,
  type CustomerDuplicateRecord,
  type CustomerDuplicates,
  type CustomerMerge,
  type DeduplicateCustomersResult,
  type DuplicateMatchField,
  formatUsPhone,
  type MergeRecords,
  queryKeys,
  type UndoMergeResult,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Archive, Search, Trash2, Undo2, Users, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { CustomerBackupButton } from '../components/customer-backup-button.js'
import { BulkActionBar, DataTable, InfiniteScrollFooter } from '../components/data-table.js'
import { MergeCustomersDialog } from '../components/merge-customers-dialog.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const MATCH_FIELD_LABEL: Record<DuplicateMatchField, string> = {
  name: 'Name',
  businessName: 'Business / identifier',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
}

function useDuplicatesQuery(q: string) {
  return useQuery({
    queryKey: queryKeys.customers.duplicates({ q }),
    queryFn: () =>
      api.get<CustomerDuplicates>('/customers/duplicates', { query: { q: q || undefined } }),
    placeholderData: keepPreviousData,
  })
}

/** Every pair of one review list, loaded a page at a time as the admin scrolls
 *  (t1346) — so no pair is ever cut off by a fixed cap. */
function usePairsList(kind: 'partial' | 'same_name' | 'similar_name', q: string) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.customers.duplicatePairs({ kind, q }),
    queryFn: ({ pageParam }) =>
      api.get<CustomerDuplicatePairsPage>('/customers/duplicates/pairs', {
        query: { kind, q: q || undefined, page: pageParam, pageSize: 50 },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  return {
    isLoading: query.isLoading,
    pairs: query.data?.pages.flatMap((p) => p.items) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    footer: {
      hasMore: Boolean(query.hasNextPage),
      isFetchingMore: query.isFetchingNextPage,
      onLoadMore: () => void query.fetchNextPage(),
    },
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return settled
}

const noMatchesDescription = (q: string, fallback: string) =>
  q ? `No pairs involve a customer matching “${q}”.` : fallback

export function CustomerCleanupPage() {
  const { can } = useAuth()
  const canWrite = can('customers:write')
  const [search, setSearch] = useState('')
  const q = useDebounced(search.trim(), 300)

  return (
    <Page>
      <PageHeader
        title="Duplicates & cleanup"
        description="Exact duplicates, potential duplicates and same-or-similar names to review and merge, a merge history you can undo, and records safe to archive or remove."
      />
      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search duplicates by name, email or phone…"
          aria-label="Search duplicates"
          className="ps-9 pe-9"
        />
        {search ? (
          <button
            type="button"
            aria-label="Clear search"
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-foreground-muted hover:text-foreground"
            onClick={() => setSearch('')}
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
      <div className="space-y-4">
        <ExactDuplicatesSection canWrite={canWrite} q={q} />
        <PartialDuplicatesSection canWrite={canWrite} q={q} />
        <SimilarNamesSection canWrite={canWrite} q={q} kind="same_name" />
        <SimilarNamesSection canWrite={canWrite} q={q} kind="similar_name" />
        <ManualMergeSection canWrite={canWrite} />
        <MergeHistorySection canWrite={canWrite} />
        <BulkCleanupSection canWrite={canWrite} />
      </div>
    </Page>
  )
}

/**
 * Exact duplicates — every stored detail identical — merge in one click.
 * The server recomputes the groups itself when the merge runs, so this is
 * never acting on a possibly-stale list; it's just a preview of what that
 * click will do.
 */
function ExactDuplicatesSection({ canWrite, q }: { canWrite: boolean; q: string }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { data, isLoading } = useDuplicatesQuery(q)
  const [confirming, setConfirming] = useState(false)
  const groups = data?.exactGroups ?? []

  const dedupe = useMutation({
    mutationFn: () => api.post<DeduplicateCustomersResult>('/customers/deduplicate'),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      setConfirming(false)
      const removed = result.merged.reduce((n, g) => n + g.mergedIds.length, 0)
      if (result.failed.length === 0) {
        toast.success(`${removed} duplicate record${removed === 1 ? '' : 's'} removed`)
      } else {
        toast.toast({
          tone: 'warning',
          title: `${removed} removed, ${result.failed.length} group${result.failed.length === 1 ? '' : 's'} could not be merged`,
          description: result.failed[0]?.reason,
        })
      }
    },
    onError: (error: Error) =>
      toast.error(
        'Could not merge duplicates',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Exact duplicates</CardTitle>
        {groups.length > 0 && canWrite && !q ? (
          <Button variant="primary" size="sm" onClick={() => setConfirming(true)}>
            Merge all exact duplicates
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {q && groups.length > 0 && canWrite ? (
          <p className="text-xs text-foreground-muted">
            Clear the search to merge all exact duplicates — that merges every group, not just
            these.
          </p>
        ) : null}
        {isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No exact duplicates"
            description={noMatchesDescription(
              q,
              'Records where every stored detail matches show up here — none right now.',
            )}
          />
        ) : (
          groups.map((group) => (
            <div
              key={group.records.map((r) => r.id).join('|')}
              className="rounded-lg border border-border p-3"
            >
              <p className="mb-2 text-xs text-foreground-muted">
                {group.records.length} identical records. The oldest is kept; the rest are removed
                and their history moves onto it.
              </p>
              <ul className="space-y-1.5">
                {group.records.map((r, idx) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{r.name}</span>
                    {idx === 0 ? (
                      <Badge tone="success">Kept</Badge>
                    ) : (
                      <Badge tone="neutral">Removed</Badge>
                    )}
                    <span className="text-xs text-foreground-muted">
                      {r.orderCount} order{r.orderCount === 1 ? '' : 's'}, {r.repairTicketCount}{' '}
                      repair{r.repairTicketCount === 1 ? '' : 's'}, {r.supportTicketCount} support
                      ticket{r.supportTicketCount === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Merge {groups.length} duplicate group{groups.length === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              Each group keeps its oldest record and removes the rest. Orders, repair tickets,
              support tickets, subscriptions, extra phone numbers and saved cards all move onto the
              record that's kept — nothing is lost. Every merge is recorded in Merge history, where
              it can be undone.
            </p>
            <div className="mt-3">
              <CustomerBackupButton />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={dedupe.isPending} onClick={() => dedupe.mutate()}>
              Merge now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/** Pairs matching on two or more details — a real name collision between two
 *  different people is possible, so these are always reviewed and merged by
 *  hand, never automatically. */
function PartialDuplicatesSection({ canWrite, q }: { canWrite: boolean; q: string }) {
  const { pairs, total, isLoading, footer } = usePairsList('partial', q)
  const [merging, setMerging] = useState<{
    a: CustomerDuplicateRecord
    b: CustomerDuplicateRecord
  } | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Potential duplicates{total > 0 ? ` (${total.toLocaleString()})` : ''}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : pairs.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No potential duplicates"
            description={noMatchesDescription(
              q,
              'Nothing currently matches on two or more details.',
            )}
          />
        ) : (
          <>
            {pairs.map((pair) => (
              <DuplicatePairCard
                key={`${pair.a.id}|${pair.b.id}`}
                a={pair.a}
                b={pair.b}
                badges={pair.matchedFields.map((f) => MATCH_FIELD_LABEL[f])}
                canWrite={canWrite}
                onMerge={() => setMerging({ a: pair.a, b: pair.b })}
              />
            ))}
            <InfiniteScrollFooter total={total} loaded={pairs.length} {...footer} />
          </>
        )}
      </CardContent>

      {merging ? (
        <MergeCustomersDialog a={merging.a} b={merging.b} onClose={() => setMerging(null)} />
      ) : null}
    </Card>
  )
}

/** One pair of records side by side, with what they matched on and a merge
 *  button — shared by the partial-match and similar-name lists. */
function DuplicatePairCard({
  a,
  b,
  badges,
  canWrite,
  onMerge,
}: {
  a: CustomerDuplicateRecord
  b: CustomerDuplicateRecord
  badges: string[]
  canWrite: boolean
  onMerge: () => void
}) {
  const navigate = useNavigate()
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex flex-wrap gap-1">
        {badges.map((label) => (
          <Badge key={label} tone="warning">
            {label}
          </Badge>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {[a, b].map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between gap-2 rounded border border-border p-2"
          >
            <div className="text-sm">
              <p className="font-medium">{r.name}</p>
              {r.businessName ? (
                <p className="text-xs text-foreground-muted">{r.businessName}</p>
              ) : null}
              {r.phone || r.email ? (
                <p className="text-xs text-foreground-muted">
                  {[r.phone ? formatUsPhone(r.phone) : null, r.email].filter(Boolean).join(' · ')}
                </p>
              ) : null}
              <p className="text-xs text-foreground-muted">
                {r.orderCount} order{r.orderCount === 1 ? '' : 's'} ·{' '}
                {r.repairTicketCount + r.supportTicketCount} ticket
                {r.repairTicketCount + r.supportTicketCount === 1 ? '' : 's'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                void navigate({ to: '/customers/$customerId', params: { customerId: r.id } })
              }
            >
              Open
            </Button>
          </div>
        ))}
      </div>
      {canWrite ? (
        <div className="mt-2">
          <Button variant="secondary" size="sm" onClick={onMerge}>
            Merge…
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/** Pairs that share only a name, or have a similar one (case, spacing,
 *  typos) — the lowest-confidence tier (t1247). A shared name is not proof
 *  of anything, so a person always looks at both records and chooses which
 *  one to keep; nothing here is ever merged automatically. */
function SimilarNamesSection({
  canWrite,
  q,
  kind,
}: {
  canWrite: boolean
  q: string
  kind: 'same_name' | 'similar_name'
}) {
  const isSame = kind === 'same_name'
  const { pairs, total, isLoading, footer } = usePairsList(kind, q)
  const [merging, setMerging] = useState<{
    a: CustomerDuplicateRecord
    b: CustomerDuplicateRecord
  } | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {isSame ? 'Same name — review' : 'Similar name — review'}
          {total > 0 ? ` (${total.toLocaleString()})` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground-muted">
          {isSame
            ? 'Records with the same name (ignoring capitals and spacing), even when the phone, email and address all differ. Lower confidence than the lists above — two different people can share a name — so open both and pick which one to keep.'
            : 'Records with a very similar name (a typo or different spelling), even when the phone, email and address all differ. Lowest confidence — open both and pick which one to keep.'}
        </p>
        {isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : pairs.length === 0 ? (
          <EmptyState
            icon={Users}
            title={isSame ? 'No same names' : 'No similar names'}
            description={noMatchesDescription(
              q,
              isSame
                ? 'No two customers share a name right now.'
                : 'No two customers have nearly the same name right now.',
            )}
          />
        ) : (
          <>
            {pairs.map((pair) => (
              <DuplicatePairCard
                key={`${pair.a.id}|${pair.b.id}`}
                a={pair.a}
                b={pair.b}
                badges={[
                  isSame
                    ? 'Same name'
                    : `Similar name (${Math.round((pair.similarity ?? 0) * 100)}%)`,
                ]}
                canWrite={canWrite}
                onMerge={() => setMerging({ a: pair.a, b: pair.b })}
              />
            ))}
            <InfiniteScrollFooter total={total} loaded={pairs.length} {...footer} />
          </>
        )}
      </CardContent>

      {merging ? (
        <MergeCustomersDialog a={merging.a} b={merging.b} onClose={() => setMerging(null)} />
      ) : null}
    </Card>
  )
}

type PickedCustomer = {
  id: string
  name: string
  businessName: string | null
  phone: string | null
  email: string | null
}

function customerSubtitle(c: PickedCustomer): string {
  return (
    [c.businessName, c.phone ? formatUsPhone(c.phone) : null, c.email]
      .filter(Boolean)
      .join(' · ') || '—'
  )
}

/** Type-ahead for one side of a manual merge. `excludeId` keeps the same
 *  customer from being picked on both sides. */
function CustomerPicker({
  label,
  value,
  excludeId,
  onSelect,
}: {
  label: string
  value: PickedCustomer | null
  excludeId?: string
  onSelect: (customer: PickedCustomer | null) => void
}) {
  const [search, setSearch] = useState('')
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.customers.list({ q: search }),
    queryFn: () =>
      api.get<{ items: PickedCustomer[] }>('/customers', { query: { q: search, pageSize: 6 } }),
    enabled: !value && search.trim().length >= 2,
  })
  const results = (data?.items ?? []).filter((c) => c.id !== excludeId)

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <p className="text-xs font-medium text-foreground-muted">{label}</p>
      {value ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm">
            <p className="font-medium">{value.name}</p>
            <p className="text-xs text-foreground-muted">{customerSubtitle(value)}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
            Change
          </Button>
        </div>
      ) : (
        <>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, or phone…"
            aria-label={label}
          />
          {results.length ? (
            <ul className="divide-y divide-border">
              {results.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="w-full px-1 py-2 text-start text-sm hover:bg-surface-sunken"
                    onClick={() => {
                      onSelect(row)
                      setSearch('')
                    }}
                  >
                    <span className="block font-medium">{row.name}</span>
                    <span className="block text-2xs text-foreground-muted">
                      {customerSubtitle(row)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : data && !isFetching && search.trim().length >= 2 ? (
            <p className="text-sm text-foreground-muted">No customers match.</p>
          ) : null}
        </>
      )}
    </div>
  )
}

/** Merge two records the detector never paired — e.g. one has only an email,
 *  the other only a phone, and the name is spelled differently. Uses the same
 *  dialog and the same server-side rules as a flagged pair. */
function ManualMergeSection({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const [first, setFirst] = useState<PickedCustomer | null>(null)
  const [second, setSecond] = useState<PickedCustomer | null>(null)
  const [merging, setMerging] = useState<MergeRecords | null>(null)

  const loadRecords = useMutation({
    mutationFn: () =>
      api.get<MergeRecords>('/customers/merge-records', {
        query: { a: first?.id, b: second?.id },
      }),
    onSuccess: setMerging,
    onError: (error: Error) =>
      toast.error(
        'Could not load these customers',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!canWrite) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Merge any two customers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground-muted">
          Pick two records by hand when the automatic check did not flag them. The same merge rules
          apply: staff accounts cannot be merged, and two records that both have their own Stripe
          billing are refused when the one being removed has saved cards or a subscription.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <CustomerPicker
            label="First customer"
            value={first}
            excludeId={second?.id}
            onSelect={setFirst}
          />
          <CustomerPicker
            label="Second customer"
            value={second}
            excludeId={first?.id}
            onSelect={setSecond}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!first || !second}
          loading={loadRecords.isPending}
          onClick={() => loadRecords.mutate()}
        >
          Merge…
        </Button>
      </CardContent>

      {merging ? (
        <MergeCustomersDialog
          a={merging.a}
          b={merging.b}
          onClose={() => {
            setMerging(null)
            setFirst(null)
            setSecond(null)
          }}
        />
      ) : null}
    </Card>
  )
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** Every merge, newest first, with an Undo that brings the removed record
 *  back and moves its history off the kept one (t1247). */
function MergeHistorySection({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [undoing, setUndoing] = useState<CustomerMerge | null>(null)

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.customers.merges({ pageSize: 25 }),
    queryFn: ({ pageParam }) =>
      api.get<{ items: CustomerMerge[]; total: number; page: number; pageSize: number }>(
        '/customers/merges',
        { query: { page: pageParam, pageSize: 25 } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const undo = useMutation({
    mutationFn: (id: string) => api.post<UndoMergeResult>(`/customers/merges/${id}/undo`),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      setUndoing(null)
      toast.success(`${result.restored.name} was restored`)
    },
    onError: (error: Error) =>
      toast.error(
        'Could not undo this merge',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const columns = useMemo<ColumnDef<CustomerMerge, unknown>[]>(
    () => [
      {
        header: 'Merged',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">{fmtDate(row.original.createdAt)}</span>
        ),
      },
      {
        header: 'Kept',
        accessorKey: 'winnerName',
        cell: ({ row }) => <span className="font-medium">{row.original.winnerName}</span>,
      },
      { header: 'Removed', accessorKey: 'loserName' },
      {
        header: 'Moved onto the kept record',
        id: 'moved',
        cell: ({ row }) => {
          const m = row.original.moved
          const parts = [
            m.orders ? `${m.orders} order${m.orders === 1 ? '' : 's'}` : null,
            m.repairTickets ? `${m.repairTickets} repair${m.repairTickets === 1 ? '' : 's'}` : null,
            m.supportTickets
              ? `${m.supportTickets} support ticket${m.supportTickets === 1 ? '' : 's'}`
              : null,
            m.subscriptions
              ? `${m.subscriptions} subscription${m.subscriptions === 1 ? '' : 's'}`
              : null,
            m.notes ? `${m.notes} note${m.notes === 1 ? '' : 's'}` : null,
          ].filter(Boolean)
          return (
            <span className="text-xs text-foreground-muted">{parts.join(', ') || 'Nothing'}</span>
          )
        },
      },
      {
        header: 'By',
        accessorKey: 'mergedByName',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">{row.original.mergedByName ?? '—'}</span>
        ),
      },
      {
        header: '',
        id: 'actions',
        cell: ({ row }) =>
          row.original.undoneAt ? (
            <Badge tone="neutral">Undone {fmtDate(row.original.undoneAt)}</Badge>
          ) : canWrite ? (
            <Button variant="ghost" size="sm" onClick={() => setUndoing(row.original)}>
              <Undo2 /> Undo
            </Button>
          ) : null,
      },
    ],
    [canWrite],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Merge history</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground-muted">
          Every merge is recorded. Undo brings the removed customer back and moves back the orders,
          tickets, subscriptions, phone numbers, cards and notes that merge moved. It is refused if
          the removed customer's phone, email or Stripe account has since been given to someone
          else, or if store credit that moved over has been spent.
        </p>
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          infinite={{
            total,
            hasMore: Boolean(hasNextPage),
            isFetchingMore: isFetchingNextPage,
            onLoadMore: () => void fetchNextPage(),
          }}
          empty={
            <EmptyState
              icon={Undo2}
              title="No merges yet"
              description="Merges you make from here on appear here."
            />
          }
        />
      </CardContent>

      <Dialog open={!!undoing} onOpenChange={(open) => !open && setUndoing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Undo this merge?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              {undoing?.loserName} comes back as its own customer, and what was moved onto{' '}
              {undoing?.winnerName} moves back. Anything added to {undoing?.winnerName} since stays
              with {undoing?.winnerName}.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setUndoing(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={undo.isPending}
              onClick={() => undoing && undo.mutate(undoing.id)}
            >
              Undo merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

type TriState = 'any' | 'missing' | 'has'
const triToBool = (v: TriState) => (v === 'any' ? undefined : v === 'missing')

const SCOPE_HELP: Record<CleanupScope, string> = {
  unused:
    'No phone, no email, and never used for a sale or a support request — safe to review and remove.',
  no_activity:
    'Anyone who was never used for a sale, rental, repair, support request, card, subscription, task or store credit — even if they have a phone or email (junk phone-book entries). Archive hides them; delete removes them for good.',
}

/** Bulk archive or delete (t0913, widened in t1247). The scope picks which
 *  records can be listed at all; the filters narrow that list; "select all
 *  matching" picks everything the filters match, not just what has scrolled
 *  into view. Customers with any history can only ever be archived. */
function BulkCleanupSection({ canWrite }: { canWrite: boolean }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [scope, setScope] = useState<CleanupScope>('unused')
  const [search, setSearch] = useState('')
  const [email, setEmail] = useState<TriState>('any')
  const [phone, setPhone] = useState<TriState>('any')
  const [origin, setOrigin] = useState('all')
  const [source, setSource] = useState('')
  const [createdBefore, setCreatedBefore] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirming, setConfirming] = useState<'delete' | 'archive' | null>(null)

  const filter = {
    scope,
    q: search || undefined,
    missingEmail: triToBool(email),
    missingPhone: triToBool(phone),
    origin: origin === 'all' ? undefined : origin,
    source: source.trim() || undefined,
    createdBefore: createdBefore || undefined,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.customers.cleanupCandidates({ ...filter, pageSize: 50 }),
    queryFn: ({ pageParam }) =>
      api.get<{ items: CleanupCandidate[]; total: number; page: number; pageSize: number }>(
        '/customers/cleanup-candidates',
        { query: { ...filter, pageSize: 50, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  // A selection only means something against the list it was made on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when a filter changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [scope, search, email, phone, origin, source, createdBefore])

  const selectAllMatching = useMutation({
    mutationFn: () =>
      api.get<CleanupCandidateIds>('/customers/cleanup-candidates/ids', { query: filter }),
    onSuccess: (result) => {
      setSelectedIds(new Set(result.ids))
      if (result.total > result.ids.length) {
        toast.toast({
          tone: 'warning',
          title: `Selected the first ${result.ids.length.toLocaleString()} of ${result.total.toLocaleString()}`,
          description: 'Run it again after these are done to pick up the rest.',
        })
      }
    },
    onError: (error: Error) =>
      toast.error('Could not select them', error instanceof ApiError ? error.message : undefined),
  })

  const chunks = <T,>(items: T[], size: number): T[][] => {
    const out: T[][] = []
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
    return out
  }

  const bulkDelete = useMutation({
    mutationFn: async () => {
      const deleted: string[] = []
      const failed: BulkDeleteCustomersResult['failed'] = []
      for (const ids of chunks([...selectedIds], 500)) {
        const r = await api.post<BulkDeleteCustomersResult>('/customers/bulk-delete', {
          ids,
          scope,
        })
        deleted.push(...r.deleted)
        failed.push(...r.failed)
      }
      return { deleted, failed }
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      setConfirming(null)
      setSelectedIds(new Set())
      if (result.failed.length === 0) {
        toast.success(
          `${result.deleted.length} record${result.deleted.length === 1 ? '' : 's'} deleted`,
        )
      } else {
        toast.toast({
          tone: 'warning',
          title: `${result.deleted.length} deleted, ${result.failed.length} could not be`,
          description: result.failed[0]?.reason,
        })
      }
    },
    onError: (error: Error) =>
      toast.error(
        'Could not delete those records',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const bulkArchive = useMutation({
    mutationFn: async () => {
      const updated: string[] = []
      const failed: BulkActiveCustomersResult['failed'] = []
      for (const customerIds of chunks([...selectedIds], 200)) {
        const r = await api.post<BulkActiveCustomersResult>('/customers/bulk-active', {
          customerIds,
          isActive: false,
        })
        updated.push(...r.updated)
        failed.push(...r.failed)
      }
      return { updated, failed }
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      setConfirming(null)
      setSelectedIds(new Set())
      toast.success(
        `${result.updated.length} record${result.updated.length === 1 ? '' : 's'} archived`,
      )
    },
    onError: (error: Error) =>
      toast.error(
        'Could not archive those records',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const columns = useMemo<ColumnDef<CleanupCandidate, unknown>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <div>
            <span className="font-medium">{row.original.name}</span>
            {!row.original.isActive ? (
              <Badge tone="neutral" className="ms-2">
                Archived
              </Badge>
            ) : null}
            {row.original.businessName ? (
              <span className="block text-xs text-foreground-muted">
                {row.original.businessName}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Phone / email',
        id: 'contact',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">
            {[row.original.phone ? formatUsPhone(row.original.phone) : null, row.original.email]
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        ),
      },
      {
        header: 'Source',
        accessorKey: 'source',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">{row.original.source ?? '—'}</span>
        ),
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
        cell: ({ row }) => (
          <span className="line-clamp-1 text-xs text-foreground-muted">
            {row.original.notes ?? '—'}
          </span>
        ),
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="text-xs text-foreground-muted">{fmtDate(row.original.createdAt)}</span>
        ),
      },
    ],
    [],
  )

  const triSelect = (
    label: string,
    value: TriState,
    onChange: (v: TriState) => void,
    missing: string,
    has: string,
  ) => (
    <Select value={value} onValueChange={(v) => onChange(v as TriState)}>
      <SelectTrigger className="w-40" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">{label}: any</SelectItem>
        <SelectItem value="missing">{missing}</SelectItem>
        <SelectItem value="has">{has}</SelectItem>
      </SelectContent>
    </Select>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk archive or delete</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={scope} onValueChange={(v) => setScope(v as CleanupScope)}>
            <SelectTrigger className="w-64" aria-label="Which records">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unused">Unused, no phone or email</SelectItem>
              <SelectItem value="no_activity">Never used — any contact details</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-foreground-muted">{SCOPE_HELP[scope]}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, phone or external ID…"
            className="max-w-xs"
          />
          {scope === 'no_activity' ? (
            <>
              {triSelect('Email', email, setEmail, 'No email', 'Has an email')}
              {triSelect('Phone', phone, setPhone, 'No phone', 'Has a phone')}
            </>
          ) : null}
          <Select value={origin} onValueChange={setOrigin}>
            <SelectTrigger className="w-40" aria-label="Origin">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All origins</SelectItem>
              {CUSTOMER_ORIGINS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Exact source…"
            className="w-44"
            aria-label="Source"
          />
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
            Created before
            <Input
              type="date"
              value={createdBefore}
              onChange={(e) => setCreatedBefore(e.target.value)}
              className="w-40"
              aria-label="Created before"
            />
          </div>
        </div>
        {canWrite && total > 0 ? (
          <div className="flex items-center gap-2 text-xs text-foreground-muted">
            <span>
              {total.toLocaleString()} match{total === 1 ? 'es' : ''}.
            </span>
            <Button
              variant="ghost"
              size="sm"
              loading={selectAllMatching.isPending}
              onClick={() => selectAllMatching.mutate()}
            >
              Select all {total.toLocaleString()}
            </Button>
          </div>
        ) : null}
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          {...(canWrite
            ? {
                getRowId: (row: CleanupCandidate) => row.id,
                selectedIds,
                onToggleRow: (id: string) =>
                  setSelectedIds((prev) => {
                    const next = new Set(prev)
                    if (next.has(id)) next.delete(id)
                    else next.add(id)
                    return next
                  }),
                onToggleAll: (ids: string[]) =>
                  setSelectedIds((prev) => (prev.size === ids.length ? new Set() : new Set(ids))),
              }
            : {})}
          infinite={{
            total,
            hasMore: Boolean(hasNextPage),
            isFetchingMore: isFetchingNextPage,
            onLoadMore: () => void fetchNextPage(),
          }}
          empty={
            <EmptyState
              icon={Users}
              title="Nothing matches"
              description="No customer records match these filters."
            />
          }
        />
      </CardContent>

      <BulkActionBar count={selectedIds.size} onClear={() => setSelectedIds(new Set())}>
        <Button variant="ghost" size="sm" onClick={() => setConfirming('archive')}>
          <Archive /> Archive
        </Button>
        <Button variant="danger" size="sm" onClick={() => setConfirming('delete')}>
          <Trash2 /> Delete
        </Button>
      </BulkActionBar>

      <Dialog open={confirming === 'archive'} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Archive {selectedIds.size.toLocaleString()} record{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              Archived customers are marked inactive: they can no longer sign in or check out
              online, and everything stays on file. Reactivate any of them later from the Customers
              page.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={bulkArchive.isPending}
              onClick={() => bulkArchive.mutate()}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirming === 'delete'} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete {selectedIds.size.toLocaleString()} record{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-foreground-muted">
              {scope === 'unused'
                ? 'These have no phone or email and have never been used for a sale or support request.'
                : 'These have never been used for a sale, rental, repair, support request, card, subscription, task or store credit — but some do have a phone number or email, which are lost with them.'}{' '}
              Deleting cannot be undone. Each record is checked again as it is deleted, and any that
              has gained activity since is skipped. Download a backup first if you might want them
              back.
            </p>
            <CustomerBackupButton />
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkDelete.isPending}
              onClick={() => bulkDelete.mutate()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
